/**
 * DashboardTour — first-time guided walkthrough.
 *
 * Renders a centred 4-step modal when the user has never completed the
 * tour (localStorage flag `haccp-tour-completed` !== "1"). Pure CSS, no
 * tour library — keeps the bundle small.
 *
 * Each step references a `data-testid` already present elsewhere in the
 * app (site-selector, nav-scan, nav-temperature, nav-settings) and pops
 * a stylised "next" prompt. Skipping or finishing both set the flag so
 * the tour never auto-fires again. Users can re-trigger it from
 * Settings → ... (future "Replay tour" button).
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, ScanLine, Thermometer, Settings as SettingsIcon, X } from "lucide-react";

const KEY = "haccp-tour-completed";

const STEPS = [
  {
    icon: MapPin,
    title: "Pick the right site first",
    body: "Multiple kitchens? The site selector in the top-right bar swaps everything — temp logs, equipment, suppliers — to that site. The selector is sticky between visits.",
  },
  {
    icon: ScanLine,
    title: "Scan & log with your camera",
    body: "Tap Scan in the sidebar (or the PWA shortcut on your phone home screen) to scan an equipment barcode and jump straight to its temperature form.",
  },
  {
    icon: Thermometer,
    title: "Out-of-range = corrective action",
    body: "Type a temperature outside the equipment's target range and we auto-open a corrective-action drawer. Out-of-range readings also fire a phone alert to site managers if they've enabled push notifications.",
  },
  {
    icon: SettingsIcon,
    title: "Configure once, log forever",
    body: "Settings → Equipment is where you set target ranges and print Code128 stickers. Settings → Sites is where you turn on the public Trust page for marketing.",
  },
];

export default function DashboardTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const done = localStorage.getItem(KEY) === "1";
      if (!done) {
        // Delay so the dashboard renders behind it for context.
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch { /* private-mode browser */ }
    return undefined;
  }, []);

  const finish = (skipped) => {
    try { localStorage.setItem(KEY, "1"); } catch { /* private-mode */ }
    setOpen(false);
    setStep(0);
    // Custom event so callers (e.g. a future "Replay tour" admin button)
    // can listen if they need to.
    window.dispatchEvent(new CustomEvent("haccp-tour-end", { detail: { skipped } }));
  };

  if (!open) return null;

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
      data-testid="dashboard-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
    >
      <div className="absolute inset-0 bg-eucalyptus-900/70 backdrop-blur-sm" />
      <div className="relative max-w-md w-full bg-white rounded-xl shadow-2xl p-6 md:p-8">
        <button
          data-testid="tour-close"
          onClick={() => finish(true)}
          aria-label="Skip tour"
          className="absolute top-3 right-3 text-charcoal-soft hover:text-charcoal"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 rounded-md bg-eucalyptus-100 text-eucalyptus-900 grid place-items-center mb-4">
          <Icon className="w-6 h-6" />
        </div>

        <div className="label-eyebrow mb-1">Step {step + 1} of {STEPS.length}</div>
        <h2
          id="tour-title"
          className="font-display font-extrabold text-xl md:text-2xl leading-tight"
          data-testid="tour-step-title"
        >
          {s.title}
        </h2>
        <p className="text-sm text-charcoal-soft mt-3 leading-relaxed">{s.body}</p>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div className="flex gap-1.5" aria-label="Tour progress">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${i <= step ? "bg-eucalyptus-700" : "bg-eucalyptus-100"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="tour-skip"
              variant="ghost"
              onClick={() => finish(true)}
              className="text-charcoal-soft"
            >
              Skip tour
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="tour-next"
                onClick={() => setStep((s) => s + 1)}
                className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
              >
                Next
              </Button>
            ) : (
              <Button
                data-testid="tour-finish"
                onClick={() => finish(false)}
                className="bg-ochre-600 hover:bg-ochre-400 text-white"
              >
                Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
